import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Trash2, ArrowLeft, Save, Check } from "lucide-react";

// Founder-gated editor for the marketing-page Testimonials + FAQ.
// Backend enforces `is_founder`; this UI check is a friendly redirect only.
export default function AdminContent() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [testimonials, setTestimonials] = useState([]);
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (user && !user.is_founder) nav("/dashboard", { replace: true });
    }, [user, nav]);

    useEffect(() => {
        api.get("/landing/content")
            .then((r) => {
                setTestimonials(r.data.testimonials || []);
                setFaqs(r.data.faqs || []);
            })
            .catch((e) => setError(e.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setError(""); setSaving(true);
        try {
            const r = await api.put("/landing/content", { testimonials, faqs });
            setTestimonials(r.data.testimonials);
            setFaqs(r.data.faqs);
            setSavedAt(Date.now());
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-[var(--text-ink)]/60">Loading…</div>;

    return (
        <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8" data-testid="admin-content-page">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <Link to="/founder" className="inline-flex items-center gap-1 text-sm text-[var(--text-ink)]/60 hover:text-[var(--brand-purple)]">
                        <ArrowLeft size={14} /> Back to Founder
                    </Link>
                    <h1 className="font-serif text-3xl sm:text-4xl mt-1">Landing content</h1>
                    <p className="text-sm text-[var(--text-ink)]/60">Edit Testimonials + FAQ shown on the marketing page.</p>
                </div>
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="admin-content-save"
                    className="btn-primary inline-flex items-center gap-2"
                >
                    {savedAt && Date.now() - savedAt < 3000 ? <Check size={14} /> : <Save size={14} />}
                    {saving ? "Saving…" : savedAt && Date.now() - savedAt < 3000 ? "Saved" : "Save changes"}
                </button>
            </div>

            {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 break-words" data-testid="admin-content-error">{error}</div>}

            <Section title="Testimonials" onAdd={() => setTestimonials((x) => [...x, { quote: "", name: "", role: "", accent: "var(--brand-purple)" }])}>
                {testimonials.map((t, i) => (
                    <div key={i} className="card-warm p-4 space-y-2" data-testid={`admin-testimonial-${i}`}>
                        <Field label="Quote">
                            <textarea rows={3} value={t.quote} onChange={(e) => update(setTestimonials, i, { quote: e.target.value })} className={inputCls} />
                        </Field>
                        <div className="grid sm:grid-cols-3 gap-2">
                            <Field label="Name"><input value={t.name} onChange={(e) => update(setTestimonials, i, { name: e.target.value })} className={inputCls} /></Field>
                            <Field label="Role / title"><input value={t.role || ""} onChange={(e) => update(setTestimonials, i, { role: e.target.value })} className={inputCls} /></Field>
                            <Field label="Accent">
                                <select value={t.accent || "var(--brand-purple)"} onChange={(e) => update(setTestimonials, i, { accent: e.target.value })} className={inputCls}>
                                    <option value="var(--brand-purple)">Purple</option>
                                    <option value="var(--brand-magenta)">Magenta</option>
                                    <option value="var(--brand-orange)">Orange</option>
                                </select>
                            </Field>
                        </div>
                        <RemoveBtn onClick={() => setTestimonials((x) => x.filter((_, j) => j !== i))} testid={`admin-testimonial-${i}-remove`} />
                    </div>
                ))}
            </Section>

            <Section title="FAQs" onAdd={() => setFaqs((x) => [...x, { q: "", a: "" }])}>
                {faqs.map((f, i) => (
                    <div key={i} className="card-warm p-4 space-y-2" data-testid={`admin-faq-${i}`}>
                        <Field label="Question"><input value={f.q} onChange={(e) => update(setFaqs, i, { q: e.target.value })} className={inputCls} /></Field>
                        <Field label="Answer"><textarea rows={4} value={f.a} onChange={(e) => update(setFaqs, i, { a: e.target.value })} className={inputCls} /></Field>
                        <RemoveBtn onClick={() => setFaqs((x) => x.filter((_, j) => j !== i))} testid={`admin-faq-${i}-remove`} />
                    </div>
                ))}
            </Section>
        </div>
    );
}

const inputCls = "w-full min-w-0 px-3 py-2 rounded-lg border border-[rgba(26,19,24,0.15)] text-sm";
const update = (setter, i, patch) => setter((x) => x.map((v, j) => (j === i ? { ...v, ...patch } : v)));

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/55 mb-1">{label}</div>
            {children}
        </label>
    );
}

function Section({ title, onAdd, children }) {
    return (
        <section className="mb-8">
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-serif text-2xl">{title}</h2>
                <button onClick={onAdd} className="btn-ghost text-sm inline-flex items-center gap-1" data-testid={`admin-add-${title.toLowerCase()}`}>
                    <Plus size={14} /> Add
                </button>
            </div>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function RemoveBtn({ onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid} className="text-xs text-[var(--text-ink)]/50 hover:text-red-600 inline-flex items-center gap-1">
            <Trash2 size={12} /> Remove
        </button>
    );
}
